import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { HISTORY_PAGE, MODULE_CATALOG } from '@vce/shared';
import { ProductThumb } from '@/components/product-thumb';
import { Badge, FIELD_LABEL, Input } from '@/components/ui/primitives';
import { cn, formatNumber } from '@/lib/utils';

export interface RunPreviewProductRow {
  productId: number;
  productName?: string;
  imageUrl?: string | null;
  fields: string[];
  /** Texto da linha secundária (ex.: score, contagem de sugestões). */
  detail: string;
}

export function RunProductPreviewPanel({
  emptyMessage,
  summary,
  products,
  selectedCount,
  excludedProductIds,
  onToggle,
  search,
  onSearchChange,
  searchPlaceholder = 'Nome, product ID ou SKU ID',
  searchEmptyMessage = 'Nenhum produto encontrado para esta busca.',
  isListFetching = false,
}: {
  emptyMessage: ReactNode;
  summary: ReactNode;
  products: RunPreviewProductRow[];
  selectedCount: number;
  excludedProductIds: Set<number>;
  onToggle(productId: number): void;
  search?: string;
  onSearchChange?(value: string): void;
  searchPlaceholder?: string;
  searchEmptyMessage?: ReactNode;
  /** Refetch da busca em andamento — não desmonta o input. */
  isListFetching?: boolean;
}) {
  const hasSearch = Boolean(search?.trim());
  const showSearch = typeof onSearchChange === 'function';

  return (
    <div className="space-y-2">
      {showSearch ? (
        <Input
          type="search"
          value={search ?? ''}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className="w-full"
        />
      ) : null}

      <div
        className={cn(
          'transition-opacity duration-150',
          isListFetching ? 'pointer-events-none opacity-60' : 'opacity-100',
        )}
        aria-busy={isListFetching || undefined}
      >
        {products.length === 0 ? (
          <div className="rounded-[12px] border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-3 py-3 text-xs text-[var(--color-muted)]">
            {hasSearch ? searchEmptyMessage : emptyMessage}
          </div>
        ) : (
          <>
            <p className="text-xs text-[var(--color-body)]">
              {summary}
              {' · '}
              <span className="font-medium text-[var(--color-ink)]">
                {formatNumber(selectedCount)} nesta execução
              </span>
              {isListFetching ? (
                <span className="text-[var(--color-muted)]"> · atualizando…</span>
              ) : null}
            </p>
            <div className="max-h-56 overflow-y-auto rounded-[12px] border border-[var(--color-hairline)]">
              <ul className="divide-y divide-[var(--color-hairline)]">
                {products.map((product) => {
                  const included = !excludedProductIds.has(product.productId);
                  return (
                    <li key={product.productId}>
                      <label
                        className={cn(
                          'flex cursor-pointer items-start gap-2 px-3 py-2 transition',
                          included
                            ? 'bg-[var(--color-surface-card)]'
                            : 'bg-[var(--color-surface-soft)] opacity-60',
                        )}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 accent-[var(--color-primary)]"
                          checked={included}
                          onChange={() => onToggle(product.productId)}
                        />
                        <ProductThumb
                          src={product.imageUrl ?? null}
                          alt={product.productName ?? `Produto ${product.productId}`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-[var(--color-ink)]">
                            {product.productName ?? `Produto #${product.productId}`}
                          </p>
                          <p className="text-[11px] text-[var(--color-muted)]">{product.detail}</p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {product.fields.map((field) => (
                              <Badge key={field} tone="neutral">
                                {FIELD_LABEL[field] ?? field}
                              </Badge>
                            ))}
                            {included ? <Badge tone="success">nesta execução</Badge> : null}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function WritePreviewEmptyMessage() {
  return (
    <>
      Nenhum produto com sugestões aprovadas.{' '}
      <Link className="font-medium text-[var(--color-primary)] hover:underline" to={HISTORY_PAGE.path}>
        Ver histórico
      </Link>{' '}
      ou{' '}
      <Link
        className="font-medium text-[var(--color-primary)] hover:underline"
        to={MODULE_CATALOG.review.path}
      >
        fila de revisão
      </Link>
      .
    </>
  );
}
